// Announcement API service for SDUI announcements
import { getAuthToken } from '../utils/auth';

const MARKETING_API = 'https://mark.tezda.tech';

// Marketing API uses @fastify/jwt which requires "Bearer <token>" format.
// The main tezda-auth and marketing API share the same JWT_SECRET so user
// JWTs verify correctly — no separate API key needed.
const fetchWithMarketingAuth = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const authToken = getAuthToken();
  if (!authToken) throw new Error('No authentication token available');
  return fetch(url, {
    ...options,
    headers: {
      ...(options.headers as Record<string, string>),
      Authorization: `Bearer ${authToken}`,
    },
  });
};

// Types
export type AnnouncementType = 'EVENT' | 'FEATURE' | 'PROMOTION' | 'UPDATE' | 'COMMUNITY';
export type AnnouncementStatus = 'DRAFT' | 'ACTIVE' | 'ARCHIVED';
export type AnnouncementAudience = 'ALL' | 'VERIFIED' | 'CREATORS' | 'SELLERS' | 'PLUS';
export type Platform = 'ios' | 'android' | 'all';

export interface SduiWidget {
  type: string;
  props: Record<string, unknown>;
  children?: SduiWidget[];
}

export interface SduiPage {
  backgroundColor?: string | null;
  backgroundImageUrl?: string | null;
  scrollable: boolean;
  widgets: SduiWidget[];
  navLabel?: string | null;
}

export interface AnnouncementTheme {
  primaryColor?: string;
  secondaryColor?: string;
  textColor?: string;
  backgroundColor?: string;
  fontFamily?: string;
}

export interface Announcement {
  _id: string;
  slug: string;
  type: AnnouncementType;
  status: AnnouncementStatus;
  title: string;
  subtitle?: string;
  thumbnailUrl: string;
  badgeText?: string | null;
  priority: number;
  startAt: string;
  expiresAt?: string | null;
  targetAudience: AnnouncementAudience;
  targetPlatforms: Platform[];
  pages?: SduiPage[];
  theme?: AnnouncementTheme | null;
  stats?: { views: number; ctaClicks: number; shares: number; dismisses: number };
  createdAt?: string;
  updatedAt?: string;
}

export interface EventMilestone {
  value: number;
  label: string;
  imageUrl: string;
}

export interface EventBadge {
  name: string;
  imageUrl: string;
  description?: string;
}

export interface EventConfig {
  categoryKey: string;
  milestones?: EventMilestone[];
  badge?: EventBadge;
  walletApiBaseUrl?: string;
}

export interface CreateAnnouncementPayload {
  slug: string;
  type: AnnouncementType;
  title: string;
  subtitle?: string;
  thumbnailUrl: string;
  badgeText?: string | null;
  priority: number;
  startAt: string;
  expiresAt?: string | null;
  targetAudience: AnnouncementAudience;
  targetPlatforms: Platform[];
  pages: SduiPage[];
  theme?: AnnouncementTheme;
  eventConfig?: EventConfig | null;
}

export type UpdateAnnouncementPayload = Partial<CreateAnnouncementPayload> & {
  status?: AnnouncementStatus;
};

// API calls
export async function fetchAnnouncements(params?: {
  status?: string;
  type?: string;
  page?: number;
  limit?: number;
}): Promise<{ data: Announcement[]; total: number }> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set('status', params.status);
  if (params?.type) qs.set('type', params.type);
  if (params?.page) qs.set('page', String(params.page));
  if (params?.limit) qs.set('limit', String(params.limit));

  const res = await fetchWithMarketingAuth(`${MARKETING_API}/announcements?${qs}`);
  if (!res.ok) throw new Error(`Failed to fetch announcements: ${res.status}`);
  return res.json();
}

export async function fetchAnnouncementDetail(id: string): Promise<Announcement> {
  const res = await fetchWithMarketingAuth(`${MARKETING_API}/announcements/${id}/page`);
  if (!res.ok) throw new Error(`Failed to fetch announcement: ${res.status}`);
  const json = await res.json();
  return json.data;
}

export async function createAnnouncement(payload: CreateAnnouncementPayload): Promise<Announcement> {
  const res = await fetchWithMarketingAuth(`${MARKETING_API}/announcements`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to create: ${res.status}`);
  }
  const json = await res.json();
  return json.data;
}

export async function updateAnnouncement(id: string, payload: UpdateAnnouncementPayload): Promise<Announcement> {
  const res = await fetchWithMarketingAuth(`${MARKETING_API}/announcements/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to update: ${res.status}`);
  }
  const json = await res.json();
  return json.data;
}

export async function deleteAnnouncement(id: string): Promise<void> {
  const res = await fetchWithMarketingAuth(`${MARKETING_API}/announcements/${id}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(`Failed to archive: ${res.status}`);
}

export async function fetchAnnouncementAnalytics(id: string): Promise<{ eventType: string; count: number; uniqueUsers: number }[]> {
  const res = await fetchWithMarketingAuth(`${MARKETING_API}/announcements/${id}/analytics`);
  if (!res.ok) throw new Error(`Failed to fetch analytics: ${res.status}`);
  const json = await res.json();
  return json.data;
}

// ============================================================
// Gate management
// ============================================================

export interface AnnouncementGate {
  enabled: boolean;
}

export interface AnnouncementAccessEntry {
  _id: string;
  username: string;
  userId: string;
  addedBy: string;
  note: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function fetchAnnouncementGate(): Promise<AnnouncementGate> {
  const res = await fetchWithMarketingAuth(`${MARKETING_API}/announcements/admin/gate`);
  if (!res.ok) throw new Error(`Failed to fetch gate: ${res.status}`);
  const json = await res.json();
  return json.data;
}

export async function setAnnouncementGate(enabled: boolean): Promise<AnnouncementGate> {
  const res = await fetchWithMarketingAuth(`${MARKETING_API}/announcements/admin/gate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled }),
  });
  if (!res.ok) throw new Error(`Failed to update gate: ${res.status}`);
  const json = await res.json();
  return json.data;
}

export async function fetchAnnouncementAccess(): Promise<AnnouncementAccessEntry[]> {
  const res = await fetchWithMarketingAuth(`${MARKETING_API}/announcements/admin/access`);
  if (!res.ok) throw new Error(`Failed to fetch access list: ${res.status}`);
  const json = await res.json();
  return json.data;
}

export async function addAnnouncementAccess(username: string, note?: string): Promise<AnnouncementAccessEntry> {
  const res = await fetchWithMarketingAuth(`${MARKETING_API}/announcements/admin/access`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, ...(note ? { note } : {}) }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || `Failed to add user: ${res.status}`);
  return json.data;
}

export async function removeAnnouncementAccess(username: string): Promise<void> {
  const res = await fetchWithMarketingAuth(`${MARKETING_API}/announcements/admin/access/${encodeURIComponent(username)}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new Error((json as any).error || `Failed to remove user: ${res.status}`);
  }
}

export interface ResetAnnouncementResult {
  userId: string;
  deleted: {
    announcementStates: number;
    announcementEntries: number;
    milestoneUnlocks: number;
    walletGiftEvents: number;
    preEntryViews: number;
    ownedEventGifts: number;
  };
}

export async function resetUserAnnouncementData(userId: string): Promise<ResetAnnouncementResult> {
  const res = await fetchWithMarketingAuth(`${MARKETING_API}/announcements/admin/access/${encodeURIComponent(userId)}/reset`, {
    method: 'POST',
  });
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new Error((json as any).error || `Failed to reset user: ${res.status}`);
  }
  const json = await res.json();
  return json.data;
}

// Widget type registry — used by the editor for validation and autocomplete
export const WIDGET_TYPES = [
  'column', 'row', 'stack', 'padding', 'container', 'expanded',
  'text', 'richText', 'image', 'icon', 'spacer', 'divider', 'badge', 'chip',
  'button', 'tapArea', 'card', 'carousel', 'listTile', 'scrollView', 'wrap',
  'countdown', 'progressBar', 'avatar', 'lottie', 'positioned',
  'progressMilestones', 'rewardGrid', 'showcaseCard', 'countdownInline',
] as const;

export const ANNOUNCEMENT_TYPES: AnnouncementType[] = ['EVENT', 'FEATURE', 'PROMOTION', 'UPDATE', 'COMMUNITY'];
export const ANNOUNCEMENT_STATUSES: AnnouncementStatus[] = ['DRAFT', 'ACTIVE', 'ARCHIVED'];
export const AUDIENCE_OPTIONS: AnnouncementAudience[] = ['ALL', 'VERIFIED', 'CREATORS', 'SELLERS', 'PLUS'];
export const PLATFORM_OPTIONS: Platform[] = ['all', 'ios', 'android'];
